import { Box, Button, Group, Modal, NumberInput, PasswordInput, Stack, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import type { IInboundOrderItem } from "../../apis/inbound-order"
import { formatQuantity } from "../../utils/format-quantity"
import { translateBackendError } from "../delivery/translate-error"
import { useInboundMutations } from "./hooks"
import { inboundItemLabel } from "./inbound-item-label"

type Props = Readonly<{
  opened: boolean
  onClose: () => void
  inboundOrderId: string | null
  items: IInboundOrderItem[]
}>

/** «Авлага дуусгах»: the PIN and one quantity per item, no hand-typed ids or JSON (O132). */
export function PickupCompleteDialog({ opened, onClose, inboundOrderId, items }: Props) {
  const { t } = useTranslation("inbound")
  const mutations = useInboundMutations()

  const form = useForm<{ pin: string; quantities: Array<number | string> }>({
    initialValues: { pin: "", quantities: items.map((it) => Number(it.dispatchedQuantity ?? it.requestedQuantity)) },
    validate: {
      pin: (v) => (/^\d{6}$/.test(v) ? null : t("pickupComplete.pinTooShort")),
      quantities: (qs) => (qs.some((q) => q === "" || Number(q) < 0) ? t("pickupComplete.quantityInvalid") : null),
    },
  })

  const handleSubmit = async (values: typeof form.values) => {
    if (!inboundOrderId) return
    // The backend takes positive quantities only; an item with 0 is simply not listed.
    const receivedItems = items.map((it, i) => ({ itemId: it.id, quantity: Number(values.quantities[i]) })).filter((r) => r.quantity > 0)
    try {
      await mutations.pickupComplete(inboundOrderId, { pin: values.pin, receivedItems })
      form.reset()
      onClose()
    } catch {
      // error shown below
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("pickupComplete.title")} size="lg">
      <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <PasswordInput label={t("pickupComplete.pinLabel")} placeholder={t("pickupComplete.pinPlaceholder")} required inputMode="numeric" maxLength={6} {...form.getInputProps("pin")} />

          <Text fz={14} fw={700}>
            {t("pickupComplete.itemsTitle")}
          </Text>
          {items.map((item, i) => (
            <Group key={item.id} justify="space-between" wrap="nowrap" align="flex-end">
              <Box miw={0}>
                <Text fz={14} fw={600}>
                  {inboundItemLabel(item, t)}
                </Text>
                <Text fz={14} c="gray.8">
                  {t("pickupComplete.requested", { qty: formatQuantity(item.requestedQuantity), unit: item.unit ?? "" })}
                </Text>
              </Box>
              <NumberInput aria-label={t("pickupComplete.quantity")} min={0} decimalScale={3} w={140} {...form.getInputProps(`quantities.${i}`)} error={undefined} />
            </Group>
          ))}
          {form.errors.quantities && (
            <Text fz={14} fw={600}>
              {form.errors.quantities}
            </Text>
          )}

          {mutations.error && (
            <Text fz={14} fw={600}>
              {translateBackendError(mutations.error)}
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("pickupComplete.cancel")}
            </Button>
            <Button type="submit" loading={mutations.submitting}>
              {t("pickupComplete.submit")}
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  )
}
